import { useState } from 'react';
import { useCallPlans, useSaveCallPlan } from '../../lib/api/hooks-products.js';
import { useRateDecks } from '../../lib/api/hooks-billing.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatMoney } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';

/**
 * Call plans: the product a customer is sold. The rate deck is how it is
 * implemented, so several plans can share one deck with different monthly fees
 * or included minutes.
 */
export function CallPlansPage() {
  const can = useSession((s) => s.can);
  const plans = useCallPlans();
  const [adding, setAdding] = useState(false);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Call plans</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            What customers are sold. Each points at a sell rate sheet that prices the calls.
          </p>
        </div>
        {can('ratedeck:write') && (
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ New plan'}
          </button>
        )}
      </div>

      {plans.error && <ErrorBanner error={plans.error} />}
      {adding && <PlanForm onDone={() => setAdding(false)} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Plan</th><th>Rate sheet</th><th>Billing</th>
                <th className="arc-td-right">Monthly</th>
                <th className="arc-td-right">Included</th>
                <th className="arc-td-right">Accounts</th>
                <th>Status</th>
              </tr>
            </thead>
            {plans.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {plans.data?.data.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="arc-stack">
                        <span style={{ fontWeight: 570 }}>{p.name}</span>
                        <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{p.key}</span>
                      </div>
                    </td>
                    <td>
                      {p.rate_deck_name
                        ? <span>{p.rate_deck_name}</span>
                        : <span className="arc-badge arc-badge-warn">no rate sheet</span>}
                    </td>
                    <td><Badge tone={p.billing_mode === 'prepaid' ? 'info' : 'muted'}>{p.billing_mode}</Badge></td>
                    <td className="arc-td-right arc-num" style={{ fontWeight: 600 }}>
                      {formatMoney(p.monthly_fee, p.currency)}
                    </td>
                    <td className="arc-td-right arc-num">
                      {p.included_minutes ? `${p.included_minutes} min` : '—'}
                    </td>
                    <td className="arc-td-right arc-num">{p.tenant_count ?? 0}</td>
                    <td><Badge tone={p.is_active ? 'ok' : 'muted'}>{p.is_active ? 'active' : 'inactive'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {plans.data !== undefined && plans.data?.data.length === 0 && (
          <Empty icon="plan" title="No call plans"
            hint="A plan wraps a rate sheet with a monthly fee — it is what appears on a quote." />
        )}
      </div>
    </>
  );
}

function PlanForm({ onDone }: { onDone: () => void }) {
  const save = useSaveCallPlan();
  const decks = useRateDecks();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [deckId, setDeckId] = useState('');
  const [fee, setFee] = useState('0');
  const [mode, setMode] = useState('prepaid');
  const step = useStepUp();

  // Only SELL decks: a cost deck is our buy price, and selling from one would
  // price every call at zero margin. The API refuses it too.
  const sellDecks = (decks.data?.data ?? []).filter((d) => d.deck_type === 'sell');

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        /**
         * What a call costs is billing, so `ratedeck:write` is a step-up permission: the API
         * refuses until the person proves they are still themselves. Without asking, the refusal
         * went into a mutation nobody rendered and Create did nothing at all (owner, 25
         * September 2026: "i clicked create and nothing happened").
         */
        void step.run(() => save.mutateAsync({
          key, name, monthly_fee: fee, billing_mode: mode,
          ...(deckId ? { sell_rate_deck_id: deckId } : {}),
        })).then((made) => { if (made) { setName(''); setKey(''); onDone(); } });
      }}
    >
      <h2 className="arc-h2" style={{ marginBottom: 14 }}>New call plan</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr 130px 130px auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-name">Plan name</label>
          <input id="cp-name" className="arc-input" required value={name}
            onChange={(e) => {
              setName(e.target.value);
              setKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
            }}
            placeholder="29c (LB, LCR)" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-key">Key</label>
          <input id="cp-key" className="arc-input" required value={key}
            onChange={(e) => setKey(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-deck">Sell rate sheet</label>
          <select id="cp-deck" className="arc-select" value={deckId}
            onChange={(e) => setDeckId(e.target.value)}>
            <option value="">Assign later</option>
            {sellDecks.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-fee">Monthly fee</label>
          <input id="cp-fee" className="arc-input" inputMode="decimal" value={fee}
            onChange={(e) => setFee(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-mode">Billing</label>
          <select id="cp-mode" className="arc-select" value={mode}
            onChange={(e) => setMode(e.target.value)}>
            <option value="prepaid">Prepaid</option>
            <option value="postpaid">Postpaid</option>
          </select>
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || step.busy}>
            {save.isPending || step.busy ? 'Saving…' : 'Create'}
          </button>
        </div>
      </div>

      {/* Whatever the API refused for, said here. A form that answers a click with silence is
          the one thing a form must never do. */}
      {save.error && <ErrorBanner error={save.error} />}
      <StepUpPrompt step={step} what="Creating a call plan">
        A call plan sets what calls cost, so it is confirmed like anything else that moves money.
      </StepUpPrompt>
    </form>
  );
}
